import { IApiClientOptions } from 'core';
import { AssureBaseApiClient, HTTP_METHOD } from './assure-base-api-client';
import {
    ComponentDto,
    ComponentMessageResponseDto,
    CreateComponentRequest,
    DeleteComponentQueryRequest,
    ListComponentsQueryRequest,
    ListComponentVersionsQueryRequest,
    UpdateComponentRequest,
} from 'dto/raptor-component.dto';
import {
    ComponentVersionDto,
} from 'dto/raptor-component-version.dto';
import { transform } from 'dto/transformer';

export class RaptorComponentsApiClient extends AssureBaseApiClient {
    constructor(options: IApiClientOptions) {
        super('raptorComponents', options);
    }

    protected getRootPath(): string {
        return 'raptor/components';
    }

    public async createComponent(
        accountId: number,
        accountUuid: string,
        params: CreateComponentRequest,
    ): Promise<ComponentDto> {
        const response = await this.send<{ component: ComponentDto }>(
            HTTP_METHOD.POST,
            this.getRequestOptionsWithAccountId(
                accountId,
                '',
                params,
                `accountUuid=${encodeURIComponent(accountUuid)}`,
            ),
        );
        return transform(ComponentDto, response.component);
    }

    public async getComponents(accountId: number, query?: ListComponentsQueryRequest): Promise<ComponentDto[]> {
        const response = await this.send<{ components: ComponentDto[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, '', this._convertToValidQuery(query)),
        );
        return transform(ComponentDto, response.components);
    }

    public async getComponent(accountId: number, uuid: string): Promise<ComponentDto> {
        const response = await this.send<{ component: ComponentDto }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, uuid),
        );
        return transform(ComponentDto, response.component);
    }

    public async updateComponent(
        accountId: number,
        uuid: string,
        params: UpdateComponentRequest,
    ): Promise<ComponentDto> {
        const response = await this.send<{ component: ComponentDto }>(
            HTTP_METHOD.PUT,
            this.getRequestOptionsWithAccountId(accountId, uuid, params),
        );
        return transform(ComponentDto, response.component);
    }

    public async deleteComponent(
        accountId: number,
        uuid: string,
        query?: DeleteComponentQueryRequest,
    ): Promise<ComponentMessageResponseDto> {
        const queryString = query?.force !== undefined ? `force=${query.force}` : undefined;
        const response = await this.send<{ message: string }>(
            HTTP_METHOD.DELETE,
            this.getRequestOptionsWithAccountId(accountId, uuid, undefined, queryString),
        );
        return transform(ComponentMessageResponseDto, response);
    }

    public async listComponentVersions(
        accountId: number,
        componentUuid: string,
        query?: ListComponentVersionsQueryRequest,
    ): Promise<ComponentVersionDto[]> {
        const response = await this.send<{ componentVersions: ComponentVersionDto[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(
                accountId,
                `${componentUuid}/versions`,
                this._convertToValidQuery(query),
            ),
        );
        return transform(ComponentVersionDto, response.componentVersions);
    }
}
